import { useMemo } from 'react'
import {
    Button,
    Drawer,
    Grid,
    Heading,
    Modal,
    PackageCard,
    Text,
    SVG,
    SVGs,
    Image,
    StarsWithTotal,
} from '../..'
import { PackageModalProps } from './props'
import { StyledPackageModal, Header, CloseButton } from './styles'

const PackageModal = ({
    title,
    packages,
    selectedPackage,
    body,
    ctaText,
    open,
    onClose,
    onOpen,
    selectedPartner,
    noReviewsText,
    onClickCTA,
    ...props
}: PackageModalProps) => {
    const header = useMemo(
        () =>
            selectedPartner && (
                <Header>
                    <Image
                        src={selectedPartner.image}
                        alt={`Image of ${selectedPartner.alias}`}
                    />
                    <Grid>
                        <Text size="l" bold primary>
                            {selectedPartner.alias}
                        </Text>
                        {selectedPartner.numberOfReviews !== undefined &&
                        selectedPartner.numberOfReviews > 0 &&
                        selectedPartner.rating !== undefined &&
                        selectedPartner.rating > 0 ? (
                            <StarsWithTotal
                                numberOfReviews={
                                    selectedPartner.numberOfReviews
                                }
                                rating={selectedPartner.rating}
                                size="m"
                            />
                        ) : (
                            <Text>{noReviewsText}</Text>
                        )}
                    </Grid>
                </Header>
            ),
        [noReviewsText, selectedPartner]
    )

    const tab = useMemo(
        () => (
            <Grid
                autoFlow="row"
                gap={16}
                css={{
                    alignContent: 'start',
                    bgc: '$primary200',
                    m: '-$3',
                    p: '$3',

                    '@desktop': {
                        mr: 0,
                    },

                    '@mobile': {
                        mb: 0,
                    },
                }}
            >
                <CloseButton>
                    <SVG src={SVGs.Close} onClick={onClose} />
                </CloseButton>
                {header}
                <Heading bold primary level={5}>
                    {title}
                </Heading>
                <PackageCard
                    multiplier={selectedPackage.multiplier}
                    price={selectedPackage.price}
                    save={selectedPackage.save}
                    service={selectedPackage.service}
                    css={{ boxShadow: '$default' }}
                />
                <Grid
                    gap={8}
                    css={{
                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(100px, 1fr))',
                    }}
                >
                    {packages.map((thisPackage, index) => (
                        <Button
                            active={thisPackage.isActive || false}
                            variant="option"
                            key={index}
                            onClick={thisPackage.onClickPackageOption}
                            size="large"
                        >
                            {thisPackage.buttonName}
                        </Button>
                    ))}
                </Grid>
                <Button
                    variant="success"
                    data-cy="packagePicker_Cta"
                    onClick={onClickCTA}
                    size="large"
                >
                    {ctaText}
                </Button>
            </Grid>
        ),
        [header, title, selectedPackage, packages, ctaText, onClickCTA, onClose]
    )

    const allContent = useMemo(
        () => (
            <Grid
                autoFlow={{ '@desktop': 'column' }}
                gap={16}
                css={{
                    '@desktop': { gridTemplateColumns: 'auto 1fr' },
                }}
            >
                {tab}
                {body}
            </Grid>
        ),
        [tab, body]
    )

    const typeOfComponent = useMemo(
        () =>
            props.type === 'modal' ? (
                <Modal
                    {...props}
                    open={open}
                    onClose={onClose}
                    onOpen={onOpen}
                    css={{
                        w: 'calc(100% - 32px)',
                    }}
                >
                    {allContent}
                </Modal>
            ) : (
                <Drawer
                    {...props}
                    open={open}
                    onClose={onClose}
                    onOpen={onOpen}
                >
                    {allContent}
                </Drawer>
            ),
        [allContent, onClose, onOpen, open, props]
    )

    return <StyledPackageModal>{typeOfComponent}</StyledPackageModal>
}

export default PackageModal

export { StyledPackageModal }
